import React, { useState } from "react";
import {
  calculateTextNumber,
  calculateTextNumberByte,
} from "../../../../util/etCetera";
import {
  validateTextLength,
  getTextLengthPlaceholder,
} from "../../../../util/textLengthValidator";
import { useUIContext } from "../../../../context/UIContext";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthContext } from "../../../../context/AuthContext";
import useFlexibleData from "../../../../hooks/useFlexibleData";
import useUnsavedChangesWarning from "../../../../hooks/useUnsavedChangesWarning";
import { gradeToConvert100 } from "../../../../util/specialCase";
import { useTranslation } from "react-i18next";
import styles from "../css/LessonStudentsTable.module.css";

export default function LessonStudentsTable({
  evaluationType,
  lessonStudentsData,
  setLessonStudentsData,
  lessonSetting,
  lessonInfo,
  updateLessonStudents,
  updateLessonStudentsBulk,
  deleteLessonStudents,
  confirmerFlexibleData,
  confirmRecordsData,
  createOrUpdateConfirm,
  isConfirmerLoggedIn,
}) {
  const { setIsLoadingModal, handleToastCenterTop, handleToastCenterBottom } =
    useUIContext();
  const { user, hasPermission } = useAuthContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [savedRows, setSavedRows] = useState([]);
  const [savedBulk, setSavedBulk] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // 저장 안 된 변경사항 경고
  useUnsavedChangesWarning(hasUnsavedChanges);

  // 사용자 권한 확인 함수 추가
  const hasUpdatePermission = () => {
    const isOwner = lessonInfo?.teachers?.some(
      (teacher) => teacher.username === user.username
    );
    return isOwner || hasPermission(null, "can_update");
  };

  const hasDeletePermission = () => {
    const isOwner = lessonInfo?.teachers?.some(
      (teacher) => teacher.username === user.username
    );
    return isOwner || hasPermission(null, "can_delete");
  };

  const {
    flexibleDataQuery: { data: flexibleData },
  } = useFlexibleData({
    schoolid: lessonSetting[0]?.schoolid,
    main_category: "convert100",
  });

  // 글자수 제한 설정 조회
  const {
    flexibleDataQuery: { data: textLengthSettings },
  } = useFlexibleData({
    schoolid: lessonSetting[0]?.schoolid,
    main_category: "extraSettings",
    sub_category: "textLength",
  });

  // useEffect(() => {
  //   if (flexibleData) {
  //     setConvert100Data(flexibleData);
  //   }
  // }, [flexibleData]);

  // 일반적인 row 데이터 변경을 처리하는 함수
  const handleTrChange = (rowIndex, event) => {
    // const { value } = event.target;
    const field = event.target.name;
    const dataName = event.target.getAttribute("data-name");
    let value;
    let updatedRows = [...lessonStudentsData];

    if (field === "eval_area_grades") {
      value = {
        ...lessonStudentsData[rowIndex].eval_area_grades,
        [dataName]: event.target.value,
      };
    } else if (field === "extra_data") {
      if (dataName === "statExclude") {
        value = {
          ...lessonStudentsData[rowIndex].extra_data,
          [dataName]: event.target.checked ? 1 : 0, // true면 1, false면 0
        };
      } else {
        value = {
          ...lessonStudentsData[rowIndex].extra_data,
          [dataName]: event.target.value,
        };
      }
    } else {
      value = event.target.value;
    }

    if (
      flexibleData?.find((r) => r.sub_category === "이수제") &&
      evaluationType === "이수제" &&
      field === "grade"
    ) {
      updatedRows = updatedRows.map((row, index) =>
        index === rowIndex
          ? {
              ...row,
              [field]: value,
              convert100: gradeToConvert100(
                value,
                flexibleData?.find((r) => r.sub_category === "이수제")
                  .data_value
              ),
              isModified: true,
            }
          : row
      );
    } else if (
      flexibleData?.find((r) => r.sub_category === "직접입력") &&
      evaluationType === "직접입력" &&
      field === "grade"
    ) {
      updatedRows = updatedRows.map((row, index) =>
        index === rowIndex
          ? {
              ...row,
              [field]: value,
              convert100:
                gradeToConvert100(
                  value,
                  flexibleData?.find((r) => r.sub_category === "직접입력")
                    ?.data_value,
                  "",
                  lessonInfo?.extra_attributes?.subjectCategory
                ) || "",
              isModified: true,
            }
          : row
      );
    } else {
      updatedRows = updatedRows.map((row, index) =>
        index === rowIndex ? { ...row, [field]: value, isModified: true } : row
      );
    }
    if (field === "eval_area_grades") {
      const lessonSettingItem = lessonSetting?.find(
        (r) => r.item_code === "evalArea100"
      );

      const settings = lessonSettingItem?.detailed_setting || [];
      const evalType = lessonSettingItem?.saved_setting;
      const totalGrades = calculateTotalGrades100(
        updatedRows[rowIndex].eval_area_grades,
        settings,
        evalType,
        lessonInfo
      );

      if (
        flexibleData?.find((r) => r.sub_category === "100점제") &&
        evaluationType === "100점제"
      ) {
        updatedRows = updatedRows.map((row, index) =>
          index === rowIndex
            ? {
                ...row,
                grade: totalGrades,
                convert100: gradeToConvert100(
                  totalGrades,
                  flexibleData?.find((r) => r.sub_category === "100점제")
                    ?.data_value,
                  "",
                  lessonInfo?.extra_attributes?.subjectCategory
                ),
              }
            : row
        );
      } else if (evaluationType === "지정평가영역") {
        updatedRows = updatedRows.map((row, index) =>
          index === rowIndex
            ? {
                ...row,
                grade: "",
              }
            : row
        );
      } else {
        updatedRows = updatedRows.map((row, index) =>
          index === rowIndex ? { ...row, grade: totalGrades } : row
        );
      }
    }

    setLessonStudentsData(updatedRows);
    setHasUnsavedChanges(true);
  };

  // 태그 변경을 처리하는 함수
  const handleTagChange = (tagName, event, rowIndex, tagIndex) => {
    const { value } = event.target; // 선택된 새로운 태그 값

    // 먼저 해당하는 row를 찾아서 복사한 다음
    const updatedRows = lessonStudentsData.map((row, index) => {
      if (index === rowIndex) {
        const updatedTags = row.tags.map((tag, idx) => {
          if (idx === tagIndex) {
            return { [tagName]: value }; // 태그 값을 업데이트
          }
          return tag;
        });

        return { ...row, tags: updatedTags, isModified: true };
      }
      return row;
    });

    // console.log("updatedRows", updatedRows);
    setLessonStudentsData(updatedRows); // 상태 업데이트
    setHasUnsavedChanges(true);
  };

  // 확인자 변경 핸들러
  const handleConfirmerChange = async (recordId, confirmerUsername) => {
    const confirmData = {
      schoolid: lessonSetting[0]?.schoolid,
      page: "dolessons",
      record_id: recordId,
      username: confirmerUsername,
    };

    try {
      console.log("전송할 데이터:", confirmData);
      await createOrUpdateConfirm.mutateAsync(confirmData);
      handleToastCenterTop("확인자가 저장되었습니다.");
    } catch (error) {
      console.error("확인자 저장 실패:", error);
      handleToastCenterTop("확인자 저장에 실패했습니다.");
    }
  };

  //이수제, 3단평가, ABC, 직접입력, 서술형, 5단평가,

  //평가항목별과 커스텀은 따로 table로...

  function renderTh() {
    switch (evaluationType) {
      case "100점제":
        return (
          <>
            {lessonSetting
              ?.find((r) => r.item_code === "evalArea100")
              ?.detailed_setting?.map((setting, index) => (
                <th
                  key={index}
                  className="text-start"
                  style={{ minWidth: "45px" }}
                >
                  {setting}
                </th>
              ))}
            <th style={{ minWidth: "45px" }}>
              {t("teacher.lessonRelated.sum")}
            </th>
            {flexibleData?.find((r) => r.sub_category === "100점제") && (
              <th style={{ minWidth: "45px" }}>
                {t("teacher.lessonRelated.convert100")}
              </th>
            )}
          </>
        );
      case "이수제":
        return (
          <>
            <th className="text-start">
              {t("teacher.lessonRelated.passFail")}
            </th>
            {flexibleData?.find((r) => r.sub_category === "이수제") && (
              <th>{t("teacher.lessonRelated.convert100")}</th>
            )}
          </>
        );
      case "3단평가":
        return (
          <>
            <th className="text-start">
              {t("teacher.lessonRelated.threeLevel")}
            </th>
          </>
        );

      case "ABC":
        return (
          <>
            <th className="text-start">ABC</th>
          </>
        );

      case "직접입력":
        return (
          <>
            <th className="text-start">
              {t("teacher.lessonRelated.directInput")}
            </th>
            {flexibleData?.find((r) => r.sub_category === "직접입력") && (
              <th>{t("teacher.lessonRelated.convert100")}</th>
            )}
          </>
        );
      case "서술형":
        return (
          <>
            <th>{t("teacher.lessonRelated.descriptive")}</th>
          </>
        );
      case "5단평가":
        return (
          <>
            <th className="text-start">
              {t("teacher.lessonRelated.fiveLevel")}
            </th>
          </>
        );
      case "지정평가영역":
        return (
          <>
            {lessonSetting
              ?.find((r) => r.item_code === "evalMethod")
              ?.extra_attributes?.evalItems?.filter(
                (item) =>
                  item.active === true &&
                  lessonInfo?.extra_attributes?.evalItems?.includes(item.id)
              )
              ?.map((setting, index) => (
                <th
                  key={index}
                  className="text-start"
                  style={{ minWidth: "55px", fontSize: "15px" }}
                >
                  {setting.evalItem}
                </th>
              ))}
          </>
        );
      default:
        return (
          <th className="text-start">{t("teacher.lessonRelated.noInfo")}</th>
        );
    }
  }

  function calculateTotalGrades100(
    eval_area_grades,
    settings,
    evalType,
    lessonInfo
  ) {
    const roundToTwoDecimals = (num) => Math.round(num * 100) / 100;

    if (evalType === "원점수기록") {
      return roundToTwoDecimals(
        settings.reduce((acc, setting) => {
          if (eval_area_grades && eval_area_grades.hasOwnProperty(setting)) {
            const value = isNaN(eval_area_grades[setting])
              ? 0
              : Number(eval_area_grades[setting]);
            const weight = lessonInfo?.eval_area_weights?.[setting] || 0;
            const validWeight = isNaN(weight) ? 0 : Number(weight);
            return acc + (value * validWeight) / 100;
          }
          return acc;
        }, 0)
      );
    } else {
      const filteredGrades = settings
        .filter(
          (setting) =>
            eval_area_grades && eval_area_grades.hasOwnProperty(setting)
        )
        .map((setting) => {
          const value = eval_area_grades[setting];
          return isNaN(value) ? 0 : Number(value);
        });

      return roundToTwoDecimals(
        filteredGrades.reduce((acc, grade) => acc + grade, 0)
      );
    }
  }

  function renderTd(
    eval_area_grades,
    rowIndex,
    grade,
    convert100 = "",
    hasConfirmer = false
  ) {
    switch (evaluationType) {
      case "100점제":
        const lessonSettingItem = lessonSetting?.find(
          (r) => r.item_code === "evalArea100"
        );
        const settings = lessonSettingItem?.detailed_setting || [];

        return (
          <>
            {settings.map((setting, index) => (
              <td key={index} className="ps-4">
                <input
                  className="input-like-span"
                  type="number"
                  data-name={setting}
                  name="eval_area_grades"
                  onChange={(e) => handleTrChange(rowIndex, e)}
                  value={
                    eval_area_grades && eval_area_grades.hasOwnProperty(setting)
                      ? eval_area_grades[setting] || ""
                      : ""
                  }
                  disabled={hasConfirmer}
                  style={
                    hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined
                  }
                />
              </td>
            ))}
            <td>{grade}</td>
            {flexibleData?.find((r) => r.sub_category === "100점제") && (
              <td style={{ width: "50px" }}>{convert100}</td>
            )}
          </>
        );

      case "이수제":
        return (
          <>
            <td className="ps-5" style={{ width: "20%" }}>
              <select
                className="select-like-span"
                name="grade"
                onChange={(e) => handleTrChange(rowIndex, e)}
                value={grade ?? ""}
                disabled={hasConfirmer}
                style={
                  hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined
                }
              >
                <option></option>
                <option value="pass">{t("teacher.lessonRelated.pass")}</option>
                <option value="fail">{t("teacher.lessonRelated.fail")}</option>
              </select>
            </td>
            {flexibleData?.find((r) => r.sub_category === "이수제") && (
              <td style={{ width: "50px" }}>{convert100}</td>
            )}
          </>
        );
      case "3단평가":
        return (
          <>
            <td className="ps-4" style={{ width: "20%" }}>
              <select
                className="select-like-span"
                name="grade"
                onChange={(e) => handleTrChange(rowIndex, e)}
                value={grade ?? ""}
                disabled={hasConfirmer}
                style={
                  hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined
                }
              >
                <option value="3">{t("teacher.lessonRelated.good")}</option>
                <option value="2">{t("teacher.lessonRelated.normal")}</option>
                <option value="1">{t("teacher.lessonRelated.effort")}</option>
                <option value="0">{t("teacher.lessonRelated.notDone")}</option>
                <option value="">
                  {t("teacher.lessonRelated.notRelated")}
                </option>
              </select>
            </td>
          </>
        );

      case "ABC":
        return (
          <>
            <td className="ps-4" style={{ width: "20%" }}>
              <select
                className="select-like-span"
                name="grade"
                onChange={(e) => handleTrChange(rowIndex, e)}
                value={grade ?? ""}
                disabled={hasConfirmer}
                style={
                  hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined
                }
              >
                <option value=""></option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
                <option value="F">F</option>
              </select>
            </td>
          </>
        );

      case "직접입력":
        return (
          <>
            <td className="ps-4" style={{ width: "20%" }}>
              <input
                className="input-like-span"
                type="text"
                name="grade"
                disabled={hasConfirmer}
                onChange={(e) => handleTrChange(rowIndex, e)}
                value={grade ?? ""}
                style={
                  hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined
                }
              />
            </td>
            {flexibleData?.find((r) => r.sub_category === "직접입력") && (
              <td style={{ width: "50px" }}>{convert100}</td>
            )}
          </>
        );
      case "서술형":
        return (
          <>
            <td className="ps-3 p-1" rowSpan={2}>
              <textarea
                type="text"
                rows="4"
                className="input-like-span"
                name="grade"
                onChange={(e) => handleTrChange(rowIndex, e)}
                value={grade ?? ""}
                placeholder={t("teacher.lessonRelated.descriptivePlaceHolder")}
                disabled={hasConfirmer}
                style={
                  hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined
                }
              />
            </td>
          </>
        );
      case "5단평가":
        return (
          <>
            <td className="ps-4" style={{ width: "20%" }}>
              <select
                className="select-like-span"
                name="grade"
                onChange={(e) => handleTrChange(rowIndex, e)}
                value={grade ?? ""}
                disabled={hasConfirmer}
                style={
                  hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined
                }
              >
                <option value="5">5</option>
                <option value="4">4</option>
                <option value="3">3</option>
                <option value="2">2</option>
                <option value="1">1</option>
                <option value="0">0</option>
                <option value="">
                  {t("teacher.lessonRelated.notRelated")}
                </option>
              </select>
            </td>
          </>
        );
      case "지정평가영역":
        const selectedEvalAreaItems = lessonSetting?.find(
          (r) => r.item_code === "evalMethod"
        );
        const evalMethod =
          selectedEvalAreaItems?.extra_attributes?.evalMethod || "";
        const evalItems =
          selectedEvalAreaItems?.extra_attributes?.evalItems
            ?.filter(
              (item) =>
                item.active === true &&
                lessonInfo?.extra_attributes?.evalItems?.includes(item.id)
            )
            ?.sort((a, b) => a.order - b.order) || [];

        return (
          <>
            {evalItems.map((item, index) => (
              <td key={index} className="ps-4">
                {insideSelectedEvalAreaTd(
                  evalMethod,
                  eval_area_grades,
                  rowIndex,
                  item,
                  hasConfirmer
                )}
              </td>
            ))}
          </>
        );

      default:
        return <td>{t("common.messages.reportToAdmin")}</td>;
    }
  }

  function insideSelectedEvalAreaTd(
    evalMethod,
    eval_area_grades,
    rowIndex,
    item,
    hasConfirmer = false
  ) {
    switch (evalMethod) {
      case "이수제":
        return (
          <>
            <select
              className="select-like-span"
              name="eval_area_grades"
              data-name={item.id}
              onChange={(e) => handleTrChange(rowIndex, e)}
              value={
                eval_area_grades && eval_area_grades.hasOwnProperty(item.id)
                  ? eval_area_grades[item.id] || ""
                  : ""
              }
              disabled={hasConfirmer}
              style={hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined}
            >
              <option></option>
              <option value="pass">{t("teacher.lessonRelated.pass")}</option>
              <option value="fail">{t("teacher.lessonRelated.fail")}</option>
            </select>
          </>
        );
      case "3단평가":
        return (
          <>
            <select
              className="select-like-span"
              name="eval_area_grades"
              data-name={item.id}
              onChange={(e) => handleTrChange(rowIndex, e)}
              value={
                eval_area_grades && eval_area_grades.hasOwnProperty(item.id)
                  ? eval_area_grades[item.id] || ""
                  : ""
              }
              disabled={hasConfirmer}
              style={hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined}
            >
              <option value="3">{t("teacher.lessonRelated.good")}</option>
              <option value="2">{t("teacher.lessonRelated.normal")}</option>
              <option value="1">{t("teacher.lessonRelated.effort")}</option>
              <option value="0">{t("teacher.lessonRelated.notDone")}</option>
              <option value="">{t("teacher.lessonRelated.notRelated")}</option>
            </select>
          </>
        );

      case "ABC":
        return (
          <>
            <select
              className="select-like-span"
              name="eval_area_grades"
              data-name={item.id}
              onChange={(e) => handleTrChange(rowIndex, e)}
              value={
                eval_area_grades && eval_area_grades.hasOwnProperty(item.id)
                  ? eval_area_grades[item.id] || ""
                  : ""
              }
              disabled={hasConfirmer}
              style={hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined}
            >
              <option value=""></option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
              <option value="F">F</option>
            </select>
          </>
        );

      case "직접입력":
        return (
          <>
            <input
              className="input-like-span"
              type="text"
              name="eval_area_grades"
              data-name={item.id}
              onChange={(e) => handleTrChange(rowIndex, e)}
              value={
                eval_area_grades && eval_area_grades.hasOwnProperty(item.id)
                  ? eval_area_grades[item.id] || ""
                  : ""
              }
              disabled={hasConfirmer}
              style={hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined}
            />
          </>
        );
      case "4단평가":
        return (
          <>
            <select
              className="select-like-span"
              name="eval_area_grades"
              data-name={item.id}
              onChange={(e) => handleTrChange(rowIndex, e)}
              value={
                eval_area_grades && eval_area_grades.hasOwnProperty(item.id)
                  ? eval_area_grades[item.id] || ""
                  : ""
              }
              disabled={hasConfirmer}
              style={hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined}
            >
              <option value="4">4</option>
              <option value="3">3</option>
              <option value="2">2</option>
              <option value="1">1</option>
              <option value="0">0</option>
              <option value="">{t("teacher.lessonRelated.notRelated")}</option>
            </select>
          </>
        );
      case "5단평가":
        return (
          <>
            <select
              className="select-like-span"
              name="eval_area_grades"
              data-name={item.id}
              onChange={(e) => handleTrChange(rowIndex, e)}
              value={
                eval_area_grades && eval_area_grades.hasOwnProperty(item.id)
                  ? eval_area_grades[item.id] || ""
                  : ""
              }
              disabled={hasConfirmer}
              style={hasConfirmer ? { backgroundColor: "#e9ecef" } : undefined}
            >
              <option value="5">5</option>
              <option value="4">4</option>
              <option value="3">3</option>
              <option value="2">2</option>
              <option value="1">1</option>
              <option value="0">0</option>
              <option value="">{t("teacher.lessonRelated.notRelated")}</option>
            </select>
          </>
        );
      default:
        return <td>{t("common.messages.reportToAdmin")}</td>;
    }
  }

  /* 개선 버전
  function renderTd(eval_area_grades, rowIndex) {
    switch (evaluationType) {
      case "100점제":
        const settings =
          lessonSetting?.find((r) => r.item_code === "evalArea100")
            ?.detailed_setting || [];
            
        const evalType = lessonSetting?.find((r) => r.item_code === "evalArea100")
          ?.saved_setting;
  
        let totalGrades = 0;
  
        if (evalType === "원점수기록") {
          totalGrades = settings.reduce((acc, setting) => {
            if (eval_area_grades && eval_area_grades.hasOwnProperty(setting)) {
              const value = isNaN(eval_area_grades[setting]) ? 0 : Number(eval_area_grades[setting]);
              const weight = lessonInfo?.eval_area_weights?.[setting] || 0;
              const validWeight = isNaN(weight) ? 0 : Number(weight);
              return acc + (value * validWeight / 100);
            }
            return acc;
          }, 0);
        } else {
          const filteredGrades = settings
            .filter(setting => eval_area_grades && eval_area_grades.hasOwnProperty(setting))
            .map(setting => {
              const value = eval_area_grades[setting];
              return isNaN(value) ? 0 : Number(value);
            });
  
          totalGrades = filteredGrades.reduce((acc, grade) => acc + grade, 0);
        }
  
        return (
          <>
            {settings.map((setting, index) => (
              <td key={index} className="ps-4">
                <input
                  className="input-like-span"
                  type="number"
                  data-name={setting}
                  name="eval_area_grades"
                  onChange={(e) => handleTrChange(rowIndex, e)}
                  value={
                    eval_area_grades && eval_area_grades.hasOwnProperty(setting)
                      ? eval_area_grades[setting]
                      : ""
                  }
                />
              </td>
            ))}
            <td>{totalGrades}</td>
          </>
        );
      default:
        return <td>정보없음</td>;
    }
  }
*/

  /* 이전 버전
  function renderTd(eval_area_grades, rowIndex) {
    switch (evaluationType) {
      case "100점제":
        const settings =
          lessonSetting?.find((r) => r.item_code === "evalArea100")
            ?.detailed_setting || [];

        const filteredGrades = settings
          .filter(
            (setting) =>
              eval_area_grades && eval_area_grades.hasOwnProperty(setting)
          )
          .map((setting) => {
            const value = eval_area_grades[setting];
            return isNaN(value) ? 0 : Number(value);
          });

        const totalGrades = filteredGrades.reduce(
          (acc, grade) => acc + grade,
          0
        );

        return (
          <>
            {settings.map((setting, index) => (
              <td key={index} className="ps-4">
                <input
                  className="input-like-span"
                  type="number"
                  data-name={setting}
                  name="eval_area_grades"
                  onChange={(e) => handleTrChange(rowIndex, e)}
                  value={
                    eval_area_grades && eval_area_grades.hasOwnProperty(setting)
                      ? eval_area_grades[setting]
                      : ""
                  }
                />
              </td>
            ))}
            <td>{totalGrades}</td>
          </>
        );
      default:
        return <td>정보없음</td>;
    }
  }
*/
  const handleSubmit = (index) => {
    const dataSet = lessonStudentsData[index];
    if (!dataSet.isModified) {
      // 변경사항이 없는 경우, 제출을 건너뜁니다.
      console.log("건너뛰기");
      handleToastCenterTop(t("common.messages.notModifed"));
      return;
    }

    if (!hasUpdatePermission()) {
      handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
      return;
    }

    // 글자수 검증
    if (
      !validateTextLength(
        dataSet,
        "comment",
        textLengthSettings?.[0]?.data_value,
        "subjectComment",
        handleToastCenterBottom,
        dataSet?.fullname + "의 세부특기사항" || ""
      )
    ) {
      return;
    }

    // 변경된 데이터 세트 처리 로직...
    // console.log("제출된 데이터 세트:", dataSet);
    setIsLoadingModal(true);
    updateLessonStudents.mutate(dataSet, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setSavedRows((prev) => [...prev, dataSet.id]);
        setTimeout(() => {
          setSavedRows([]);
        }, 1000);
        // 저장 후 남은 변경사항 체크
        const hasRemainingChanges = lessonStudentsData.some(
          (d, i) => i !== index && d.isModified
        );
        if (!hasRemainingChanges) {
          setHasUnsavedChanges(false);
        }
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error); // 일단 콘솔에 에러를 출력합니다.
        console.error("위와 같은 에러가 났으니 고치삼");
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
    // 성공적으로 제출 후, 해당 데이터 세트의 isModified 상태를 초기화합니다.
    setLessonStudentsData((currentDataSets) =>
      currentDataSets.map((dataSet, dataSetIndex) =>
        dataSetIndex === index ? { ...dataSet, isModified: false } : dataSet
      )
    );
  };

  const handleSubmitAll = () => {
    const dataArray = lessonStudentsData.filter(
      (data) => data.isModified === true
    );

    if (dataArray.length < 1) {
      // 변경사항이 없는 경우, 제출을 건너뜁니다.
      console.log("건너뛰기");
      handleToastCenterTop(t("common.messages.notModifed"));
      return;
    }

    if (!hasUpdatePermission()) {
      handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
      return;
    }

    // 글자수 검증 (배열 전체)
    if (
      !validateTextLength(
        dataArray,
        "comment",
        textLengthSettings?.[0]?.data_value,
        "subjectComment",
        handleToastCenterBottom,
        "세부특기사항"
      )
    ) {
      return;
    }

    // 변경된 데이터 세트 처리 로직...
    // console.log("제출된 데이터 세트:", dataArray);
    setIsLoadingModal(true);
    updateLessonStudentsBulk.mutate(dataArray, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setSavedBulk(true);
        setTimeout(() => {
          setSavedBulk(false);
        }, 1000);

        // 성공적으로 제출 후, 해당 데이터 세트의 isModified 상태를 초기화합니다.
        setLessonStudentsData((currentDataSets) =>
          currentDataSets.map((data) =>
            data.isModified === true ? { ...data, isModified: false } : data
          )
        );
        setHasUnsavedChanges(false);
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error); // 일단 콘솔에 에러를 출력합니다.
        console.error("위와 같은 에러가 났으니 고치삼");
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  const handleDelete = (row) => {
    const id = row.id;

    if (!hasDeletePermission()) {
      handleToastCenterTop(t("common.messages.ownerDeletePermission"));
      return;
    }

    setIsLoadingModal(true);
    deleteLessonStudents.mutate(id, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setConfirmDelete([]);
        queryClient.invalidateQueries(["lessonStudents"]);
        setLessonStudentsData((rows) => rows.filter((row) => row.id !== id));
      },
      onError: (error) => {
        setIsLoadingModal(false);
        setConfirmDelete([]);
        console.error(error); // 일단 콘솔에 에러를 출력합니다.
        console.error("위와 같은 에러가 났으니 고치삼");
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  const handleCancelDelete = () => {
    setConfirmDelete([]);
  };

  const handleConfirmDelete = (row) => {
    setConfirmDelete((prev) => [...prev, row.id]);
  };

  return (
    <>
      <form>
        <table className={`table text-center align-middle ${styles.table}`}>
          <thead>
            <tr>
              <th style={{ minWidth: "60px" }}>
                {t("common.labels.schoolStage")}
              </th>
              <th style={{ minWidth: "60px" }}>
                {t("common.labels.studentYear")}
              </th>
              <th style={{ minWidth: "60px" }}>{t("common.labels.class")}</th>
              <th style={{ minWidth: "60px" }}>
                {t("common.user.fullname") || "이름"}
              </th>
              {renderTh()}
              <th style={{ minWidth: "320px" }}>
                {t("teacher.lessonRelated.specificComment")}
              </th>
              <th style={{ minWidth: "70px" }}>
                {t("teacher.lessonRelated.remark")}
              </th>
              {confirmerFlexibleData && confirmerFlexibleData.length > 0 && (
                <th style={{ minWidth: "90px" }}>확인자</th>
              )}
              <th
                style={{ minWidth: "50px", width: "50px" }}
                title={
                  t("teacher.lessonRelated.statExcludeDescription") ||
                  "수강자수에서 제외되어, 성적표, 생활기록부에 과목평균, 표준편차, 석차 등 계산에서 제외됩니다."
                }
              >
                {t("teacher.lessonRelated.statExclude") || "통계제외"}
              </th>
              <th style={{ minWidth: "90px" }}>
                {hasUpdatePermission() && (
                  <button
                    type="button"
                    onClick={handleSubmitAll}
                    className="btn btn-sm p-1 btn-primary"
                  >
                    {savedBulk
                      ? t("common.labels.savedWell") || "저장완료"
                      : t("common.labels.saveAll") || "모두저장"}
                  </button>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {lessonStudentsData &&
              lessonStudentsData.length > 0 &&
              lessonStudentsData.map((student, rowIndex) => {
                // 확인자 존재 여부 체크
                const currentConfirm = confirmRecordsData?.find(
                  (record) =>
                    record.record_id.toString() === student.id.toString()
                );
                const hasConfirmer = currentConfirm?.username ? true : false;
                const savedUsername = currentConfirm?.username || "";
                const confirmerOptions =
                  confirmerFlexibleData?.[0]?.data_value?.options || [];

                return (
                  <React.Fragment key={`tr-${student.id}`}>
                    <tr
                      key={student.id}
                      className="align-middle"
                      style={{ fontSize: "14px" }}
                    >
                      <td>{student.hierarchyOne}</td>
                      <td>{student.hierarchyTwo}</td>
                      <td>{student.hierarchyThree}</td>
                      <td>{student.fullname}</td>
                      {renderTd(
                        student.eval_area_grades,
                        rowIndex,
                        student.grade,
                        student.convert100,
                        hasConfirmer
                      )}

                      <td className="ps-3 p-1" rowSpan={2}>
                        <textarea
                          type="text"
                          rows="4"
                          className="input-like-span"
                          name="comment"
                          value={student?.comment || ""}
                          onChange={(e) => handleTrChange(rowIndex, e)}
                          placeholder={getTextLengthPlaceholder(
                            textLengthSettings?.[0]?.data_value,
                            "subjectComment"
                          )}
                          disabled={hasConfirmer}
                          style={
                            hasConfirmer
                              ? { backgroundColor: "#e9ecef" }
                              : undefined
                          }
                        />
                      </td>
                      <td className="p-0" rowSpan={2}>
                        <textarea
                          type="text"
                          rows="4"
                          className="input-like-span"
                          name="extra_data"
                          data-name="remark"
                          value={student?.extra_data?.remark || ""}
                          onChange={(e) => handleTrChange(rowIndex, e)}
                          disabled={hasConfirmer}
                          style={
                            hasConfirmer
                              ? { backgroundColor: "#e9ecef" }
                              : undefined
                          }
                        />
                      </td>
                      {confirmerFlexibleData &&
                        confirmerFlexibleData.length > 0 && (
                          <td className="p-1" rowSpan={2}>
                            {!isConfirmerLoggedIn ? (
                              <div>
                                {confirmerOptions.find(
                                  (opt) => opt.username === savedUsername
                                )?.fullname || ""}
                              </div>
                            ) : (
                              <select
                                key={`${student.id}-${savedUsername}`}
                                className="form-select form-select-sm"
                                value={savedUsername}
                                onChange={(e) =>
                                  handleConfirmerChange(
                                    student.id,
                                    e.target.value
                                  )
                                }
                                style={{ fontSize: "0.875rem" }}
                              >
                                <option value="">선택</option>
                                {confirmerOptions.map((option) => (
                                  <option
                                    key={option.username}
                                    value={option.username}
                                  >
                                    {option.fullname}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        )}
                      <td
                        style={{ border: "none", padding: "0px" }}
                        title={
                          t("teacher.lessonRelated.statExcludeDescription") ||
                          "수강자수에서 제외되어, 성적표, 생활기록부에 과목평균, 표준편차, 석차 등 계산에서 제외됩니다."
                        }
                      >
                        <input
                          type="checkbox"
                          name="extra_data"
                          data-name="statExclude"
                          checked={
                            student?.extra_data?.statExclude === 1 || false
                          }
                          onChange={(e) => handleTrChange(rowIndex, e)}
                          className={styles.warningCheckbox}
                          style={
                            hasConfirmer
                              ? {
                                  transform: "scale(1.7)",
                                  backgroundColor: "#e9ecef",
                                }
                              : { transform: "scale(1.7)" }
                          }
                          disabled={hasConfirmer}
                        />
                      </td>
                      <td className="p-0" rowSpan={2}>
                        {t("common.labels.textLength")}:{" "}
                        {(() => {
                          const textInfo = calculateTextNumber(
                            student.comment,
                            textLengthSettings?.[0]?.data_value,
                            "subjectComment"
                          );
                          return typeof textInfo === "string" ? (
                            textInfo
                          ) : (
                            <span
                              style={{
                                color: textInfo.color,
                                fontWeight: textInfo.color ? "bold" : "normal",
                              }}
                            >
                              {textInfo.text}
                            </span>
                          );
                        })()}
                        <br />
                        {calculateTextNumberByte(student.comment)}
                        <br />
                        {hasUpdatePermission() && (
                          <button
                            type="button"
                            onClick={() => handleSubmit(rowIndex)}
                            className="btn btn-sm p-1 btn-primary m-0"
                            disabled={hasConfirmer}
                          >
                            {savedRows.includes(student.id)
                              ? t("common.labels.savedWell") || "저장완료"
                              : t("common.labels.save") || "저장"}
                          </button>
                        )}
                        {hasDeletePermission() &&
                          (confirmDelete.includes(student.id) ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleDelete(student)}
                                className="btn btn-sm p-1 btn-danger ms-1"
                                disabled={hasConfirmer}
                              >
                                {t("common.labels.confirm") || "확인"}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCancelDelete()}
                                className="btn btn-sm p-1 btn-secondary m-0"
                                disabled={hasConfirmer}
                              >
                                {t("common.labels.cancel") || "취소"}
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleConfirmDelete(student)}
                              className="btn btn-sm p-1 btn-warning ms-1"
                              disabled={hasConfirmer}
                            >
                              {t("common.labels.delete") || "삭제"}
                            </button>
                          ))}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: "3px solid #000" }}>
                      <th className="pb-0 pt-0 align-middle">
                        {t("common.labels.tag")}
                      </th>
                      <td colSpan={6} className="pb-1 pt-1 full-width-colspan">
                        <div className="d-flex" style={{ fontSize: "14px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-end",
                              gap: "5px",
                            }}
                          >
                            {student?.tags?.map((tag, tagIndex) => {
                              const tagName = Object.keys(tag)[0];
                              return (
                                <div
                                  key={tagIndex}
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <div
                                    className="text-center"
                                    style={{ marginBottom: "1px" }}
                                  >
                                    {tagName}
                                  </div>
                                  <select
                                    className="form-control p-1"
                                    style={
                                      hasConfirmer
                                        ? {
                                            fontSize: "14px",
                                            backgroundColor: "#e9ecef",
                                          }
                                        : { fontSize: "14px" }
                                    }
                                    value={tag[tagName] || ""}
                                    name="tags"
                                    onChange={(e) =>
                                      handleTagChange(
                                        tagName,
                                        e,
                                        rowIndex,
                                        tagIndex
                                      )
                                    }
                                    disabled={hasConfirmer}
                                  >
                                    <option value="1">
                                      {t("common.labels.veryWeak")}
                                    </option>
                                    <option value="2">
                                      {t("common.labels.weak")}
                                    </option>
                                    <option value="3">
                                      {t("common.labels.normal")}
                                    </option>
                                    <option value="4">
                                      {t("common.labels.strong")}
                                    </option>
                                    <option value="5">
                                      {t("common.labels.veryStrong")}
                                    </option>
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </form>
    </>
  );
}
